import React, { useMemo, useCallback } from "react";
import { DataTable, Hyperlink, fmt, type Column, type SortingState } from "@racoon/ui";
import { ConnectProto, RouteStatus } from "@racoon/api";
import { RouteStatusBadge, getStatusDisplay } from "./RouteStatusBadge";
import { IcFlag } from "@racoon/icons";
import { User } from "~/shared/api/user/user.grpc";
import { fullName } from "~/shared/helpers";
import { getDispatcherInitials } from "../utils/getDispatcherInitials";
import { useTableSettings } from "~/shared/hooks/useTableSettings";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { Namespace, useT, TFn } from "@racoon/i18n";
import { fromTimestamp } from "@racoon/lib";
import { useCustomerCardModal } from "~/features/customer-card/hooks/use-customer-card-modal";
import { usePickupLocationCard } from "~/features/pickup-location-modal/hooks/use-pickup-location-card";
import { useUserCard } from "~/pages/dashboard/users/ui/use-user-card";
import { useShowOnDashboard } from "~/shared/hooks/useShowOnDashboard";
import { useJobCard } from "~/pages/dashboard/orders/ui/use-job-card";
import { isJobEditable } from "~/shared/lib/job-status";

// Function to get date for sorting: plan_start_at, if not - created_at
const getSortDate = (row: ConnectProto.Route): number => {
  const planStartAt = row.planStartAt;
  if (planStartAt) {
    const date = fromTimestamp(planStartAt);
    if (date) {
      const time = date.getTime();
      if (!isNaN(time) && time > 0) return time;
    }
  }
  const createdAt = row.createdAt;
  if (createdAt) {
    const date = fromTimestamp(createdAt);
    if (date) {
      const time = date.getTime();
      if (!isNaN(time) && time > 0) return time;
    }
  }
  return 0;
};

const compareRoutesByPlanStartOrCreated = (
  a: ConnectProto.Route,
  b: ConnectProto.Route,
): number => {
  const dateA = getSortDate(a);
  const dateB = getSortDate(b);
  return dateB - dateA; // desc - newest first
};

const toRouteStatus = (status: ConnectProto.RouteStatus) =>
  `ROUTE_STATUS_${ConnectProto.RouteStatus[status]}` as RouteStatus;

export const sortRoutesByPlanStartOrCreated = (
  data: ConnectProto.Route[],
): ConnectProto.Route[] => {
  return [...data].sort(compareRoutesByPlanStartOrCreated);
};

export const RoutesTable: React.FC<{
  data?: ConnectProto.Route[];
  loading?: boolean;
  highlightShiftId?: string | null;
  targetRoute?: ConnectProto.Route;
  selectedRouteId?: string | number | bigint | null;
  onRouteSelect?: (route: ConnectProto.Route) => void;
  dispatchersMap?: Map<number, User>;
  /**
   * The rows arrive sorted by the server: with server-side pagination they are one page,
   * and client sorting would reorder only it.
   */
  manualSorting?: boolean;
  /** The sort to show when the table mounts; the page owns it across views. */
  sorting?: SortingState;
  onSortChange?: (sorting: SortingState) => void;
  /** Loads the next page when the last row scrolls into view. */
  onEndReached?: () => void;
  /** True while the next page is loading. */
  loadingMore?: boolean;
}> = ({
  data = [],
  loading,
  highlightShiftId,
  targetRoute,
  selectedRouteId,
  onRouteSelect,
  dispatchersMap,
  manualSorting,
  sorting,
  onSortChange,
  onEndReached,
  loadingMore,
}) => {
  const { selectedCompanyId } = useSelectedCompany();
  const { columnConfig, handleColumnConfigChange } = useTableSettings(
    "routes",
    selectedCompanyId || undefined,
  );
  const t = useT();

  // A route names five other entities; each one opens its own card over the
  // registry, except the plate, which shows its truck on the dashboard map.
  const { openSingle: openCustomerCard } = useCustomerCardModal({
    providerId: selectedCompanyId ?? undefined,
  });
  const { openLocation, pickupLocationCard } = usePickupLocationCard();
  const { openUser, userCard } = useUserCard();
  const { showTruck } = useShowOnDashboard();

  // The registry shows the route's first job — the same one the columns below
  // report the site and the customer of.
  const { openJob } = useJobCard();

  // Function to get dispatcher initials
  const getDispatcherInitialsCallback = useCallback(
    (authorId: number | undefined): string => {
      return getDispatcherInitials(authorId, dispatchersMap);
    },
    [dispatchersMap],
  );

  // Function to determine if route should be highlighted
  const shouldHighlightRoute = (route: ConnectProto.Route) => {
    if (!highlightShiftId || !targetRoute) return false;

    // Check that route is related to the problem shift
    const isRelatedToShift =
      route.driverOnShift?.id === targetRoute.driverOnShift?.id ||
      route.driverOnShift?.driverId === targetRoute.driverOnShift?.driverId ||
      route.driverOnShift?.truckId === targetRoute.driverOnShift?.truckId;

    // Check that route is not completed (status is not COMPLETED)
    const isIncomplete = route.status !== ("ROUTE_STATUS_COMPLETED" as any);

    return isRelatedToShift && isIncomplete;
  };

  const columns = useMemo<Column<ConnectProto.Route>[]>(
    () => [
      {
        id: "author",
        header: t(Namespace.Routes, "colAuthor"),
        width: 80,
        sortable: true,
        accessor: (r) => {
          if (r.createdByAi) return t(Namespace.Routes, "aiBadge");
          return getDispatcherInitialsCallback(r.authorId ? Number(r.authorId) : undefined);
        },
        cell: (info) => {
          const r = info.row.original;
          const isHighlighted = shouldHighlightRoute(r);
          const label = info.getValue() as string;

          if (r.createdByAi) {
            return (
              <div className="flex items-center gap-2">
                <span className="h-8 w-8 flex flex-shrink-0 items-center justify-center rounded-full bg-brand-purple text-sm text-white font-medium">
                  {label}
                </span>
                {isHighlighted && (
                  <span className="text-red-500 text-lg animate-pulse" title="Проблемный маршрут">
                    ⚠
                  </span>
                )}
              </div>
            );
          }

          return (
            <div className="flex items-center gap-2">
              <span className="h-8 w-8 flex flex-shrink-0 items-center justify-center rounded-full bg-red-500 text-sm text-white font-bold">
                {label}
              </span>
              {isHighlighted && (
                <span className="text-red-500 text-lg animate-pulse" title="Проблемный маршрут">
                  ⚠
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: "route",
        header: t(Namespace.Routes, "colRoute"),
        sortable: true,
        width: 120,
        accessor: (r) => `M${r.id}`,
        cell: (info) => {
          const r = info.row.original;
          return (
            <Hyperlink onOpen={() => onRouteSelect && onRouteSelect(r)}>
              M{r.id.toString()}
            </Hyperlink>
          );
        },
      },

      {
        id: "plate",
        header: t(Namespace.Routes, "colPlateNumber"),
        width: 120,
        sortable: true,
        accessor: (r) => r.driverOnShift?.truck?.plateNumber ?? "",
        cell: (info) => {
          const r = info.row.original;
          const isHighlighted = shouldHighlightRoute(r);
          const plate = info.getValue() as string;
          const truckId = r.driverOnShift?.truck?.id;
          return (
            <div
              className={
                isHighlighted
                  ? "bg-red-50 px-2 py-1 rounded border-l-4 border-red-500 font-semibold"
                  : ""
              }
            >
              {plate && truckId !== undefined ? (
                <Hyperlink onOpen={() => showTruck(truckId)}>{plate}</Hyperlink>
              ) : (
                plate
              )}
            </div>
          );
        },
      },
      {
        id: "driver",
        header: t(Namespace.Routes, "driver"),
        width: 180,
        sortable: true,

        accessor: (r) => fullName(r.driverOnShift?.driver),
        cell: (info) => {
          const driver = info.row.original.driverOnShift?.driver;
          const name = (info.getValue() as string) || "";
          if (!name || !driver) return "—";
          return <Hyperlink onOpen={() => openUser(driver)}>{name}</Hyperlink>;
        },
      },
      {
        id: "status",
        header: t(Namespace.Routes, "colStatus"),
        sortable: true,
        width: 120,
        accessor: (r) => r.status,
        cell: (info) => {
          const route = info.row.original;
          const jobs = route.jobs || [];
          return (
            <RouteStatusBadge
              status={toRouteStatus(route.status)}
              jobs={jobs}
              completedJobs={
                jobs.filter(
                  (job: any) =>
                    `JOB_STATUS_${ConnectProto.JobStatus[job.status]}` === "JOB_STATUS_COMPLETED",
                ).length
              }
              onStatusClick={
                jobs.length > 1 ? () => onRouteSelect && onRouteSelect(route) : undefined
              }
            />
          );
        },
      },
      {
        id: "created",
        header: t(Namespace.Routes, "colCreated"),
        width: 160,
        key: "created_at",
        sortable: true,
        accessor: (r) => fromTimestamp(r.createdAt),
        cell: (info) => {
          const v = info.getValue() as string | Date;
          return v ? fmt.dateTime(v) : "—";
        },
      },
      {
        id: "start",
        header: t(Namespace.Routes, "colStarted"),
        width: 160,
        sortable: true,
        accessor: (r) => fromTimestamp(r.factStartAt),
        cell: (info) => {
          const v = info.getValue() as string | Date;
          return v ? fmt.dateTime(v) : "—";
        },
      },
      {
        id: "end",
        header: t(Namespace.Routes, "colFinished"),
        width: 160,
        sortable: true,
        accessor: (r) => fromTimestamp(r.factEndAt),
        cell: (info) => {
          const v = info.getValue() as string | Date;
          return v ? fmt.dateTime(v) : "—";
        },
      },
      {
        id: "activeOrder",
        header: t(Namespace.Routes, "colActiveOrder"),
        sortable: true,

        width: 140,
        accessor: (r) => (r.jobs?.[0]?.id ? `Z${r.jobs[0].id}` : ""),
        cell: (info) => {
          const job = info.row.original.jobs?.[0];
          const label = (info.getValue() as string) || "";
          // Same rule as the orders registry: a finished or cancelled job has
          // no edit dialog to open, so its number stays plain text.
          if (!label || !job || !isJobEditable(job.status)) return label || "—";
          return <Hyperlink onOpen={() => openJob(job)}>{label}</Hyperlink>;
        },
      },
      {
        id: "geozone",
        header: t(Namespace.Routes, "colGeozone"),
        width: 80,
        sortable: true,
        accessor: (r) => r.jobs?.[0]?.pickupLocation?.geoGroup ?? "",
      },
      {
        id: "object",
        header: t(Namespace.Routes, "colOrderObject"),
        width: 80,
        sortable: true,
        accessor: (r) => r.jobs?.[0]?.pickupLocation?.name ?? "",
        cell: (info) => {
          const location = info.row.original.jobs?.[0]?.pickupLocation;
          const name = (info.getValue() as string) || "";
          if (!name || !location) return name || "—";
          return <Hyperlink onOpen={() => openLocation(location)}>{name}</Hyperlink>;
        },
      },
      {
        id: "service_receiver",
        header: t(Namespace.Routes, "colCustomer"),
        width: 80,
        sortable: true,
        accessor: (r) => r.jobs?.[0]?.serviceReceiver?.name ?? "",
        cell: (info) => {
          const customer = info.row.original.jobs?.[0]?.serviceReceiver;
          const name = (info.getValue() as string) || "";
          if (!name || !customer) return name || "—";
          return <Hyperlink onOpen={() => openCustomerCard(customer)}>{name}</Hyperlink>;
        },
      },
      {
        id: "pseudo",
        header: t(Namespace.Routes, "colPseudo"),
        width: 80,
        sortable: true,
        accessor: (r) => r?.isPseudo,
        cell: (info) =>
          info.getValue() ? (
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full text-brand-blue">
              <IcFlag />
            </span>
          ) : (
            ""
          ),
      },
    ],
    [
      getDispatcherInitialsCallback,
      shouldHighlightRoute,
      t,
      showTruck,
      openUser,
      openJob,
      openLocation,
      openCustomerCard,
    ],
  );
  return (
    <>
      <DataTable<ConnectProto.Route>
        columns={columns}
        data={data}
        loading={loading}
        onEndReached={onEndReached}
        loadingMore={loadingMore}
        rowKey={(r) => String(r.id)}
        manualSorting={manualSorting}
        onSortChange={onSortChange}
        initialSort={sorting?.[0] && { id: sorting[0].id, dir: sorting[0].desc ? "desc" : "asc" }}
        zebra
        className="min-w-[100%] flex-1 min-h-0"
        selectable={false}
        selectedRowKeys={selectedRouteId ? [String(selectedRouteId)] : []}
        columnConfig={columnConfig}
        onColumnConfigChange={handleColumnConfigChange}
        namespace={Namespace.Routes}
      />
      {pickupLocationCard}
      {userCard}
    </>
  );
};

// Export simplified columns for CSV export
export const getRoutesExportColumns = (
  t: TFn,
  getDispatcherInitials?: (authorId: number | undefined) => string,
): Column<ConnectProto.Route>[] => {
  return [
    {
      id: "author",
      header: t(Namespace.Routes, "colAuthor"),
      accessor: (r) => {
        if (r.createdByAi) return t(Namespace.Routes, "aiBadge");
        return getDispatcherInitials
          ? getDispatcherInitials(r.authorId ? Number(r.authorId) : undefined)
          : r.authorId?.toString() || "—";
      },
    },
    {
      id: "route",
      header: t(Namespace.Routes, "colRoute"),
      accessor: (r) => `M${r.id}`,
    },
    {
      id: "plate",
      header: t(Namespace.Routes, "colPlateNumber"),
      accessor: (r) => r.driverOnShift?.truck?.plateNumber ?? "",
    },
    {
      id: "driver",
      header: t(Namespace.Routes, "driver"),
      accessor: (r) => fullName(r.driverOnShift?.driver) || "—",
    },
    {
      id: "status",
      header: t(Namespace.Routes, "colStatus"),
      accessor: (r) => getStatusDisplay(t, toRouteStatus(r.status)).text,
    },
    {
      id: "created",
      header: t(Namespace.Routes, "colCreated"),
      key: "created_at",
      accessor: (r) => {
        const date = fromTimestamp(r.createdAt);
        return date ? fmt.dateTime(date) : "—";
      },
    },
    {
      id: "start",
      header: t(Namespace.Routes, "colStarted"),
      accessor: (r) => {
        const date = fromTimestamp(r.factStartAt);
        return date ? fmt.dateTime(date) : "—";
      },
    },
    {
      id: "end",
      header: t(Namespace.Routes, "colFinished"),
      accessor: (r) => {
        const date = fromTimestamp(r.factEndAt || r.planEndAt);
        return date ? fmt.dateTime(date) : "—";
      },
    },
    {
      id: "activeOrder",
      header: t(Namespace.Routes, "colActiveOrder"),
      accessor: (r) => (r.jobs?.[0]?.id ? `Z${r.jobs[0].id}` : ""),
    },
    {
      id: "geozone",
      header: t(Namespace.Routes, "colGeozone"),
      accessor: (r) => r.jobs?.[0]?.pickupLocation?.geoGroup ?? "",
    },
    {
      id: "object",
      header: t(Namespace.Routes, "colOrderObject"),
      accessor: (r) => r.jobs?.[0]?.pickupLocation?.name ?? "",
    },
    {
      id: "service_receiver",
      header: t(Namespace.Routes, "colCustomer"),
      accessor: (r) => r.jobs?.[0]?.serviceReceiver?.name ?? "",
    },
    {
      id: "pseudo",
      header: t(Namespace.Routes, "colPseudo"),
      accessor: (r) => (r?.isPseudo ? t(Namespace.Routes, "yes") : t(Namespace.Routes, "no")),
    },
  ];
};
